/**
 * Milestones and outcomes.
 *
 * ── STUDENT REPORTED, AND SAID SO EVERYWHERE ─────────────────────────────
 * A milestone is marked complete by the student in the app. The outcome
 * milestones (starting a program, declaring a major, completing an
 * internship, landing a first full-time role) additionally require the
 * student to say where, and that note stays private to the student and their
 * mentor. P3 captures these outcomes; it does not verify them against a
 * registrar or an employer. The distinction is verification, not capability,
 * and it is stated on the card, in the footnote and in the trace drawer,
 * never softened.
 *
 * ── THE FLOOR IS ON PEOPLE, NOT EVENTS ───────────────────────────────────
 * Twelve completions by four students is a figure about four people. The
 * data layer withholds the total, the per-student figure and the weekly
 * curve until ten distinct students have completed something, and this panel
 * renders that as a threshold state. The meter showing the distance to go
 * appears only where that distance is known: a live tenant's withheld figure
 * arrives with no count (the Worker sends none under the floor), so it draws
 * no meter rather than a false zero. Past the floor, a breakdown can still
 * have no group of ten (a milestone counts the students who completed it),
 * and that is its own state, never the floor's.
 *
 * ── A LIVE TENANT (Sep 11 2026) ──────────────────────────────────────────
 * The figures are read each hour from the milestone records, through
 * p3-milestones-reader. They span each student's record rather than a
 * window, and a student's milestones follow the student. While the reader is
 * not bound the section says milestones are not counted per institution yet;
 * when the last refresh could not read them, it says that instead.
 */

import {
  Card,
  CardBody,
  CardFootnote,
  CardHeader,
  CardTitle,
  Chip,
  Denominator,
  LeadFigure,
  Minor,
  ProvenanceNote,
  SectionHead,
  StateCard,
  SubGroupLabel,
} from "~/app/components/primitives";
import { SECTION_LEDE, SECTION_TITLE } from "~/app/lib/admin-nav";
import { formatCount, formatPercent } from "~/app/lib/format";
import { annotationPositions, type CohortView, type Distribution } from "~/app/lib/demo-data";
import { RankedList, SERIES, TrendArea } from "~/app/panels/panel-charts";
import { AscentMark } from "~/app/components/marks";
import { K, count, type Cell } from "~/lib/suppression";

/**
 * Career timelines (Pipe D; Thomas, Sep 12 2026: "add a Timeline card now").
 *
 * The schools and roles a student adds to their own Career Timeline, counted
 * by the web store and never read: the store returns counts, not entries. Its
 * rules are the card's states. A count of entries is a figure about the
 * students behind it, so nothing publishes under ten of them; and a group of
 * rows (by stage, by kind) publishes whole or not at all, because one hidden
 * row is the total minus the others.
 */
function TimelinesCard({ view }: { view: CohortView }) {
  const { timelines, institution } = view;
  const where = `at ${institution.legalName}, ${view.windowLabel}`;
  const keepers: Cell<number> =
    timelines.withTimeline.kind === "value"
      ? count(timelines.withTimeline.value.numerator)
      : timelines.withTimeline;
  const publish = timelines.entries.total.kind === "value" && timelines.entries.total.value > 0;
  const known = timelines.withTimeline.kind === "value" || timelines.studentsRaw > 0;
  const absence = timelines.unread
    ? { title: "Not read on the last refresh", body: timelines.unread }
    : timelines.notMeasured
      ? { title: "Not counted per institution yet", body: timelines.notMeasured }
      : null;

  const group = (id: string, title: string, distribution: Distribution, color: string) => (
    <div className="e-timeline-group" aria-labelledby={id}>
      <SubGroupLabel id={id}>{title}</SubGroupLabel>
      {distribution.allSuppressed ? (
        <p className="e-note">Shown once every row stands for ten students or more.</p>
      ) : (
        <RankedList
          distribution={distribution}
          label={`Career timeline entries ${title.toLowerCase()} ${where}`}
          color={color}
        />
      )}
    </div>
  );

  return (
    <div className="e-grid">
      <Card span="full">
        <CardHeader aside={<Chip tone="warn">Student reported</Chip>}>
          <CardTitle sub="The schools and roles students add to their own timeline.">
            Career timelines
          </CardTitle>
        </CardHeader>
        <CardBody>
          {absence ? (
            <StateCard variant="not-measured" title={absence.title}>
              {absence.body}
            </StateCard>
          ) : publish ? (
            <div className="e-timeline">
              <div className="e-vstack e-timeline-lead">
                <LeadFigure
                  cell={keepers}
                  format={formatCount}
                  label="Students keeping one"
                  provenance={timelines.provenance}
                />
                <Denominator
                  cell={timelines.withTimeline}
                  of="of your students keep a Career Timeline"
                  noun="students"
                  label="Share keeping a timeline"
                />
                <Minor cell={timelines.entries.total} format={formatCount} label="Entries" />
              </div>
              {group("timeline-stage", "By stage", timelines.byStage, SERIES[1])}
              {group("timeline-kind", "By kind", timelines.byKind, SERIES[0])}
            </div>
          ) : (
            <StateCard
              variant="building"
              title="Published at ten students keeping a timeline"
              progress={
                known
                  ? { have: timelines.studentsRaw, need: K, noun: "students keeping one" }
                  : undefined
              }
            >
              A timeline is kept by one student about themselves, so its counts
              publish once ten of your students keep one.
            </StateCard>
          )}
        </CardBody>
        <CardFootnote>
          <p className="e-note">Added by the student, not verified.</p>
          <ProvenanceNote provenance={timelines.provenance} />
        </CardFootnote>
      </Card>
    </div>
  );
}

export function MilestonesPanel({ view }: { view: CohortView }) {
  const { milestones, institution } = view;

  /* A live tenant whose milestones are not counted per institution yet, or
     whose last refresh could not read the milestone records: the section head
     and one sentence in the snapshot's words, and no card that could read as
     a zero or borrow the sample's figures. Career timelines come from another
     source, so their card keeps its own state underneath. */
  const absence = milestones.notMeasured
    ? { title: "Not measured per institution yet", body: milestones.notMeasured }
    : milestones.unread
      ? { title: "Not read on the last refresh", body: milestones.unread }
      : null;
  if (absence) {
    return (
      <section className="e-sec e-print-page">
        <SectionHead
          id="milestones"
          title={SECTION_TITLE.milestones}
          lede={SECTION_LEDE.milestones}
        />
        <StateCard variant="not-measured" title={absence.title}>
          {absence.body}
        </StateCard>
        <TimelinesCard view={view} />
      </section>
    );
  }

  /* A published total means ten or more students stand behind it, even when
     their count is withheld because the students it leaves out are fewer
     than ten. A real zero is a value too, and takes the threshold state. */
  const publish = milestones.completed.kind === "value" && milestones.completedRaw > 0;
  const where = `at ${institution.legalName}, ${view.windowLabel}`;
  const known = milestones.students.kind === "value" || milestones.studentsRaw > 0;
  const outcomesKnown =
    milestones.outcomeStudents.kind === "value" || milestones.outcomeStudentsRaw > 0;

  const floor = (title: string, body: string) => (
    <StateCard
      variant="building"
      title={title}
      progress={
        known
          ? { have: milestones.studentsRaw, need: K, noun: "students completing one" }
          : undefined
      }
    >
      {body}
    </StateCard>
  );

  /* Past the floor, with no group of ten yet. No meter: the distance to go is
     per group, and no group is named. */
  const groupsUnder = (title: string, body: string) => (
    <StateCard variant="building" title={title}>
      {body}
    </StateCard>
  );

  return (
    <section className="e-sec e-print-page">
      <SectionHead
        id="milestones"
        title={SECTION_TITLE.milestones}
        lede={SECTION_LEDE.milestones}
      />

      <div className="e-grid">
        <Card span={2}>
          <CardHeader>
            <CardTitle sub="The milestones your students mark complete most often.">
              Top milestones
            </CardTitle>
          </CardHeader>
          <CardBody>
            {publish && !milestones.top.allSuppressed ? (
              <RankedList
                distribution={milestones.top}
                label={`Milestones completed by students ${where}`}
                color={SERIES[0]}
              />
            ) : publish ? (
              groupsUnder(
                "No milestone has reached ten students yet",
                "Each milestone is counted by the students who completed it, and each is under ten so far. The list fills in as your students complete more.",
              )
            ) : (
              floor(
                "Milestone reporting begins at ten completing students",
                "Each milestone is counted by the students who completed it, so the list publishes once ten of your students have marked one complete. It is held until then, not zero.",
              )
            )}
          </CardBody>
          <CardFootnote>
            <p className="e-note">{milestones.top.footnote}</p>
            <ProvenanceNote
              provenance={milestones.provenance}
              suppressed={milestones.top.hiddenCount > 0}
            />
          </CardFootnote>
        </Card>

        <Card solo className="e-card-art">
          <CardHeader>
            <CardTitle sub={`In the Mentee Milestone Framework, ${milestones.span}.`}>
              Milestones completed
            </CardTitle>
          </CardHeader>
          <CardBody>
            <AscentMark />
            {publish ? (
              <div className="e-vstack">
                <LeadFigure
                  cell={milestones.completed}
                  format={formatCount}
                  label="Milestones completed"
                  provenance={milestones.provenance}
                />
                <div className="e-minor-row" style={{ gridTemplateColumns: "1fr 1fr" }}>
                  <Minor
                    cell={milestones.students}
                    format={formatCount}
                    label="Students completing one"
                  />
                  <Minor
                    cell={milestones.perStudent}
                    format={(n) => n.toFixed(1)}
                    label="Per completing student"
                  />
                </div>
              </div>
            ) : (
              floor(
                "Published at ten completing students",
                "A count of completions is a count of the students behind it.",
              )
            )}
          </CardBody>
          <CardFootnote>
            <p className="e-note">
              Reported by the student in the app. A signal, not a record.
            </p>
            <ProvenanceNote provenance={milestones.provenance} />
          </CardFootnote>
        </Card>
      </div>

      <div className="e-grid">
        <Card variant="chart" span={2} plot="half">
          <CardHeader>
            <CardTitle sub="Weeks end on Sunday. The current partial week is not drawn.">
              Milestones completed over time
            </CardTitle>
          </CardHeader>
          <CardBody>
            {milestones.byWeek.drawable.length >= 2 ? (
              <div className="e-plot">
                <TrendArea
                  points={milestones.byWeek.drawable}
                  annotations={annotationPositions(milestones.byWeek)}
                  label={`Milestones completed by week ${where}`}
                  color={SERIES[2]}
                  unit="milestones"
                />
              </div>
            ) : publish ? (
              groupsUnder(
                "The curve needs two published weeks",
                "A week is plotted once ten students stand behind its running total, and a line needs two such weeks to draw.",
              )
            ) : (
              floor(
                "The curve starts at ten completing students",
                "A running total of completions is withheld until ten of your students have completed a milestone, because the count is a count of people. The curve begins at the first week that publishes.",
              )
            )}
          </CardBody>
          <CardFootnote>
            <ProvenanceNote provenance={milestones.byWeek.provenance} />
            {milestones.byWeek.annotations.length > 0 ? (
              <p className="e-note">
                Dashed rules are markers your team added:{" "}
                {milestones.byWeek.annotations.map((a) => a.label).join(", ")}. P3
                draws them and asserts nothing about them.
              </p>
            ) : null}
          </CardFootnote>
        </Card>

        <Card solo>
          <CardHeader>
            <CardTitle sub="Every student in view, by milestones completed.">
              Progress per student
            </CardTitle>
          </CardHeader>
          <CardBody>
            {publish && !milestones.progress.allSuppressed ? (
              <RankedList
                distribution={milestones.progress}
                label={`Students by milestones completed ${where}`}
                color={SERIES[2]}
              />
            ) : publish ? (
              groupsUnder(
                "Every group is under ten students",
                "Students are grouped by how many milestones they have completed, and no group has ten yet.",
              )
            ) : (
              floor(
                "Published at ten completing students",
                "A count of completions is a count of the students behind it.",
              )
            )}
          </CardBody>
          <CardFootnote>
            <p className="e-note">{milestones.progress.footnote}</p>
            <ProvenanceNote
              provenance={milestones.provenance}
              suppressed={milestones.progress.hiddenCount > 0}
            />
          </CardFootnote>
        </Card>

        {/* Outcomes as a funnel of stages (Thomas, Sep 9 2026: "funnel/progress
            and where students end up over time"). The four outcome milestones
            in the order a student reaches them, each row the students who
            reported it. No conversion arithmetic: the stages are not nested. */}
        <Card span="full">
          <CardHeader aside={<Chip tone="warn">Student reported</Chip>}>
            <CardTitle sub="The four outcome milestones, in the order a student reaches them.">
              Student outcomes
            </CardTitle>
          </CardHeader>
          <CardBody>
            {publish && !milestones.outcomes.allSuppressed ? (
              <div className="e-vstack">
                <div className="e-minor-row" style={{ gridTemplateColumns: "1fr 1fr" }}>
                  <Minor
                    cell={milestones.outcomeStudents}
                    format={formatCount}
                    label="Students reporting an outcome"
                  />
                  {/* FIELD MATCH. Counted over the students whose first role
                      can be compared with their own profile industry, never
                      over the cohort, so the note says which.

                      It is absent far more often than not, and the bar is
                      higher than the floor looks: rate() holds the complement
                      to ten as well as the share, so a split needs about
                      twenty comparable students, not ten. With one student
                      platform-wide at this milestone in Sep 2026, absent is
                      the correct reading for every institution today, and the
                      note says what it takes rather than implying a number is
                      missing. The industry behind it is decided inside
                      p3-milestones-reader, the only place a completion note
                      may be read. */}
                  <Minor
                    cell={milestones.fieldMatch}
                    format={(r) => formatPercent(r.rate)}
                    label="Working in their own field"
                    unavailableNote="Counted over your students whose first role can be compared with the industry on their profile. Published once neither side of the share would stand for fewer than ten students."
                  />
                </div>
                <RankedList
                  distribution={milestones.outcomes}
                  label={`Outcome milestones reported by students ${where}`}
                  color={SERIES[3]}
                />
              </div>
            ) : milestones.outcomeStudents.kind === "value" && milestones.outcomeStudentsRaw >= K ? (
              groupsUnder(
                "No outcome has reached ten students yet",
                "Each outcome is counted by the students who reported it, and each is under ten so far.",
              )
            ) : (
              <StateCard
                variant="building"
                title="Published at ten reporting students"
                progress={
                  outcomesKnown
                    ? {
                        have: milestones.outcomeStudentsRaw,
                        need: K,
                        noun: "students reporting one",
                      }
                    : undefined
                }
              >
                An outcome milestone is reported by one student about
                themselves, so the breakdown publishes once ten of your students
                have reported one.
              </StateCard>
            )}
          </CardBody>
          <CardFootnote>
            <p className="e-note">{milestones.outcomes.footnote}</p>
            <ProvenanceNote
              provenance={milestones.outcomesProvenance}
              suppressed={milestones.outcomes.hiddenCount > 0}
            />
          </CardFootnote>
        </Card>
      </div>

      <TimelinesCard view={view} />
    </section>
  );
}
